import ThreeSixtyView from "../ThreeSixtyView/ThreeSixtyView";
import { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { darkMode, notAvailableImg } from "../../constants/common-constants";
import { useLocation } from "react-router-dom";
import VehicleDetailsZoomableImageModal from "../VehicleDetailsZoomableImageModal/VehicleDetailsZoomableImageModal";
import SVGSelector from "../common-components/SVGSelector";
import { useSelector } from "react-redux";
import { getEdgeClass } from "../../utils";
import { useTranslation } from "react-i18next";

const getYouTubeVideoId = (url) => {
  const match = url.match(/embed\/([a-zA-Z0-9_-]+)/);
  return match?.[1] || null;
};

const VehicleImageSwiper = ({
  vehicleDetails = [],
  panoramaImages,
  vehicleName,
  youTubeEmbedUrl,
}) => {
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});
  const { edge = false } = useSelector((state) => state?.uiConfig) || {};
  const { search } = useLocation();
  const { t } = useTranslation();
  const queryParams = new URLSearchParams(search);
  const vehicleId = parseInt(queryParams.get("vehicle-id"));
  const [visibleImageCount, setVisibleImageCount] = useState(5);
  const videoId = youTubeEmbedUrl ? getYouTubeVideoId(youTubeEmbedUrl) : null;
  const youtubeThumbnail = videoId
    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    : null;

  const loadMoreImagesIfNeeded = (index) => {
    const adjustedIndex = youTubeEmbedUrl ? index - 1 : index;
    if (
      adjustedIndex + 4 >= visibleImageCount &&
      visibleImageCount < vehicleDetails?.length
    ) {
      setVisibleImageCount((prev) =>
        Math.min(prev + 5, vehicleDetails?.length),
      );
    }
  };
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const thumbnailSwiperRef = useRef(null);
  useEffect(() => {
    const swiper = thumbnailSwiperRef.current?.swiper;
    if (!swiper) return;

    const updateNavState = () => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    };

    swiper.on("transitionEnd", updateNavState);
    updateNavState();
    return () => {
      swiper.off("transitionEnd", updateNavState);
    };
  }, [vehicleDetails]);

  const handleThumbnailClick = (index) => {
    const mainSwiperIndex = youTubeEmbedUrl ? index : index;
    setSelectedIndex(mainSwiperIndex);
    loadMoreImagesIfNeeded(mainSwiperIndex);
    if (swiperInstance) {
      swiperInstance.slideTo(mainSwiperIndex);
    }
  };
  useEffect(() => {
    return () => {
      setLoadedImages({});
    };
  }, [vehicleId]);

  useEffect(() => {
    setSelectedIndex(0);
    if (swiperInstance && swiperInstance.slideTo) {
      swiperInstance.slideTo(0, 0);
    }
    //eslint-disable-next-line
  }, [vehicleDetails, search]);

  const handleImageLoad = (index) => {
    setLoadedImages((prev) => ({ ...prev, [index]: true }));
  };
  const [isZoomModalVisible, setIsZoomModalVisible] = useState(false);
  const [vehicleItem, setVehicleItem] = useState(null);
  const [isOverlayModalVisible, setIsOverlayModalVisible] = useState(false);

  const handleZoomModalToggle = (item) => {
    setVehicleItem(item);
    setIsZoomModalVisible(!isZoomModalVisible);
  };

  const handleOverlayModalToggle = () => {
    setIsOverlayModalVisible(!isOverlayModalVisible);
  };

  useEffect(() => {
    if (thumbnailSwiperRef?.current) {
      thumbnailSwiperRef?.current?.navigation?.init();
      thumbnailSwiperRef?.current?.navigation?.update();
    }
  }, []);

  useEffect(() => {
    const swiperInstance = thumbnailSwiperRef.current?.swiper;
    if (!swiperInstance) return;

    const handleSlideChange = () => {
      const index = swiperInstance.activeIndex;
      loadMoreImagesIfNeeded(index);

      // Update navigation buttons
      const shadowRoot = document.querySelector(
        "#am-marketplace #main-container",
      )?.shadowRoot;
      const nextButton = shadowRoot?.querySelector(
        ".thumbnail-swiper-next-btn",
      );
      const prevButton = shadowRoot?.querySelector(
        ".thumbnail-swiper-prev-btn",
      );

      if (nextButton) {
        nextButton.classList.toggle(
          "swiper-button-disabled",
          swiperInstance.isEnd,
        );
        nextButton.disabled = swiperInstance.isEnd;
      }
      if (prevButton) {
        prevButton.classList.toggle(
          "swiper-button-disabled",
          swiperInstance.isBeginning,
        );
        prevButton.disabled = swiperInstance.isBeginning;
      }
    };

    swiperInstance.on("slideChange", handleSlideChange);

    return () => {
      swiperInstance.off("slideChange", handleSlideChange);
    };
    //eslint-disable-next-line
  }, [visibleImageCount, vehicleDetails?.length]);

  useEffect(() => {
    const swiperInstance = thumbnailSwiperRef.current?.swiper;
    if (!swiperInstance) return;

    const shadowRoot = document.querySelector(
      "#am-marketplace #main-container",
    )?.shadowRoot;
    const nextButton = shadowRoot?.querySelector(".thumbnail-swiper-next-btn");
    const prevButton = shadowRoot?.querySelector(".thumbnail-swiper-prev-btn");

    if (!nextButton || !prevButton) return;

    const handleNextClick = (e) => {
      if (swiperInstance.isEnd) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      swiperInstance.slideNext();
      loadMoreImagesIfNeeded(swiperInstance.activeIndex + 1);
    };

    const handlePrevClick = (e) => {
      if (swiperInstance.isBeginning) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      swiperInstance.slidePrev();
    };

    nextButton.addEventListener("click", handleNextClick);
    prevButton.addEventListener("click", handlePrevClick);

    return () => {
      nextButton.removeEventListener("click", handleNextClick);
      prevButton.removeEventListener("click", handlePrevClick);
    };
    //eslint-disable-next-line
  }, [thumbnailSwiperRef.current, visibleImageCount]);

  useEffect(() => {
    const swiperInstance = thumbnailSwiperRef.current?.swiper;
    if (!swiperInstance) return;

    const handleThumbnailIndexChange = () => {
      const index = swiperInstance.activeIndex;
      loadMoreImagesIfNeeded(index);
    };

    swiperInstance.on("transitionEnd", handleThumbnailIndexChange);

    return () => {
      swiperInstance.off("transitionEnd", handleThumbnailIndexChange);
    };
    //eslint-disable-next-line
  }, [thumbnailSwiperRef.current?.swiper, vehicleDetails, visibleImageCount]);

  const getImageUrl = (url, width, height) => {
    if (!url) return notAvailableImg;

    const base = url.split("&bg=")[0];
    const bg = darkMode ? "1E1E1E" : "ffffff";
    return `${base}&w=${width}&h=${height}&bg=${bg}`;
  };
  const [isDragging, setIsDragging] = useState(false);
  const iframeWrapperRef = useRef(null);

  return (
    <>
      <div
        className="w-[60%] max-[1299px]:w-2/4 max-lg:w-full"
        key={vehicleDetails?.length + 1}
      >
        <div className={`swiper ${getEdgeClass(edge, "rounded-md")}`}>
          <Swiper
            modules={[Navigation]}
            key={vehicleDetails?.length + 1}
            onSwiper={setSwiperInstance}
            onSlideChange={(swiper) => {
              const index = swiper.activeIndex;
              setSelectedIndex(index);
              loadMoreImagesIfNeeded(index);
            }}
            spaceBetween={0}
            slidesPerView={1}
            pagination={false}
            mousewheel={false}
          >
            {youTubeEmbedUrl && (
              <SwiperSlide key="video">
                <div
                  className="h-full w-full max-h-[650px] relative"
                  ref={iframeWrapperRef}
                  onTouchStart={() => setIsDragging(true)}
                  onTouchEnd={() => setIsDragging(false)}
                  onMouseDown={() => setIsDragging(true)}
                  onMouseUp={() => setIsDragging(false)}
                >
                  {isDragging && <div className="absolute inset-0 z-10" />}
                  <iframe
                    className="w-full h-full rounded-t-lg"
                    src={youTubeEmbedUrl}
                    title="YouTube video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </SwiperSlide>
            )}
            {vehicleDetails?.slice(0, visibleImageCount)?.map((item, index) => {
              const actualIndex = youTubeEmbedUrl ? index + 1 : index;
              return (
                <SwiperSlide key={actualIndex}>
                  <div className="h-full w-full max-h-[650px] relative">
                    {!loadedImages[actualIndex] && (
                      <div
                        className={`absolute top-0 left-0 h-full w-full bg-gray-300 animate-pulse ${getEdgeClass(edge)}`}
                      ></div>
                    )}
                    <img
                      className={`${getEdgeClass(edge, "rounded-t-lg")} ${
                        loadedImages[actualIndex] ? "opacity-100" : "opacity-0"
                      } h-auto object-cover max-lg:w-full w-full aspect-[4/3]`}
                      src={
                        getImageUrl(item?.downloadUrl, 650, 550) ||
                        notAvailableImg
                      }
                      alt={`${vehicleName} - ${index + 1}`}
                      onError={(e) => {
                        e.target.src = notAvailableImg;
                      }}
                      onClick={() => handleZoomModalToggle(item)}
                      onLoad={() => handleImageLoad(actualIndex)}
                    />
                    {index === 0 && panoramaImages?.length ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                        <button
                          className="px-4 py-2 bg-white text-black text-lg font-semibold rounded-lg shadow-lg"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOverlayModalToggle();
                          }}
                        >
                          {t("/vehicleDetails.Open in 360°")}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
        <div className="pt-5" key={vehicleDetails?.length + 1}>
          <Swiper
            ref={thumbnailSwiperRef}
            spaceBetween={12}
            slidesPerView="auto"
            freeMode={true}
            watchSlidesProgress={true}
            slideToClickedSlide={true}
            onSlideChange={(swiper) => {
              const index = swiper.activeIndex;
              loadMoreImagesIfNeeded(index);
            }}
            navigation={{
              nextEl: ".thumbnail-swiper-next-btn",
              prevEl: ".thumbnail-swiper-prev-btn",
              disabledClass: "swiper-button-disabled",
            }}
            modules={[Navigation]}
          >
            {youtubeThumbnail && (
              <SwiperSlide key="video-thumb" style={{ width: "auto" }}>
                <div
                  className="h-[9rem] w-[12.5rem] relative max-sm:h-[6rem] max-sm:w-[8rem] cursor-pointer"
                  onClick={() => handleThumbnailClick(0)}
                >
                  <img
                    src={youtubeThumbnail}
                    alt={`${vehicleName} - YouTube Preview`}
                    className={`${getEdgeClass(edge)} h-full w-full object-cover`}
                  />
                </div>
              </SwiperSlide>
            )}

            {vehicleDetails?.slice(0, visibleImageCount)?.map((item, index) => {
              const actualIndex = youTubeEmbedUrl ? index + 1 : index;
              return (
                <SwiperSlide key={actualIndex} style={{ width: "auto" }}>
                  <div
                    className="h-[9rem] w-[12.5rem] relative max-sm:h-[6rem] max-sm:w-[8rem]"
                    onClick={() => handleThumbnailClick(actualIndex)}
                  >
                    {!loadedImages[actualIndex] && (
                      <div
                        className={`absolute top-0 left-0 h-full w-full bg-gray-300 animate-pulse ${getEdgeClass(edge)}`}
                      ></div>
                    )}
                    <img
                      className={`${getEdgeClass(edge)} h-full w-full object-cover cursor-pointer transition-opacity duration-300 ${
                        loadedImages[actualIndex] ? "opacity-100" : "opacity-0"
                      } ${
                        actualIndex === selectedIndex &&
                        loadedImages[actualIndex]
                          ? "border-4 primary-color"
                          : ""
                      }`}
                      src={
                        getImageUrl(item?.downloadUrl, 300, 220) ||
                        notAvailableImg
                      }
                      alt={`${vehicleName} - ${index + 1}`}
                      onError={(e) => {
                        e.target.src = notAvailableImg;
                      }}
                      onLoad={() => handleImageLoad(actualIndex)}
                    />
                    {index === 0 && panoramaImages?.length ? (
                      <div
                        className={`absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 ${getEdgeClass(edge)}`}
                        onClick={() => handleThumbnailClick(index)}
                      >
                        <SVGSelector name="360-view" />
                      </div>
                    ) : null}
                  </div>
                </SwiperSlide>
              );
            })}
            <button
              className={`thumbnail-swiper-prev-btn ${isBeginning ? "swiper-button-disabled" : ""}`}
              disabled={isBeginning}
              onClick={() => {
                const swiper = thumbnailSwiperRef.current?.swiper;
                if (swiper && !isBeginning) {
                  swiper.slidePrev();
                }
              }}
              aria-label="Previous Slide"
              aria-disabled={isBeginning}
            >
              <SVGSelector
                name="arrow-left-svg"
                pathFill={"var(--text-white-black)"}
                aria-hidden="true"
              />
            </button>
            <button
              className={`thumbnail-swiper-next-btn ${isEnd ? "swiper-button-disabled" : ""}`}
              disabled={isEnd}
              onClick={() => {
                const swiper = thumbnailSwiperRef.current?.swiper;
                if (swiper && !isEnd) {
                  swiper.slideNext();
                  loadMoreImagesIfNeeded(swiper.activeIndex + 1);
                }
              }}
              aria-label="Next Slide"
              aria-disabled={isEnd}
            >
              <SVGSelector
                name="arrow-right-svg"
                pathFill={"var(--text-white-black)"}
                aria-hidden="true"
              />
            </button>
          </Swiper>
        </div>
      </div>
      <VehicleDetailsZoomableImageModal
        isVisible={isZoomModalVisible}
        handleModalToggle={() => setIsZoomModalVisible(!isZoomModalVisible)}
        edge={edge}
        vehicleItem={vehicleItem}
        vehicleDetails={vehicleDetails}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
      />
      {isOverlayModalVisible && (
        <ThreeSixtyView
          isVisible={isOverlayModalVisible}
          handleModalToggle={handleOverlayModalToggle}
          panoramaImages={panoramaImages}
        />
      )}
    </>
  );
};
export default VehicleImageSwiper;
